/**
 * Proactive Token Refresher
 * Periodically checks JWT expiry and refreshes ~skew seconds before expiry.
 * Uses the same refresh endpoint as the interceptor with `_skipAuth` to avoid header injection.
 */

import axios from 'axios';
import authService from './authService';

let intervalId = null;
let inFlight = false;
let onExpiredCallback = null;

const jitter = (minMs, maxMs) =>
  Math.floor(Math.random() * (maxMs - minMs + 1)) + minMs;

export const refreshOnce = async (backendUrl) => {
  if (inFlight) return true;
  inFlight = true;
  try {
    const refreshToken = authService.getRefreshToken();
    if (!refreshToken) {
      return false;
    }
    const res = await axios.post(
      `${backendUrl}/api/v1/auth/refresh`,
      { refreshToken },
      { _skipAuth: true }
    );
    const { jwt, refreshToken: newRefreshToken } = res.data || {};
    if (!jwt) throw new Error('No JWT in proactive refresh response');
    authService.setToken(jwt);
    if (newRefreshToken) authService.setRefreshToken(newRefreshToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${jwt}`;
    return true;
  } catch (err) {
    console.error(
      '[tokenRefresher] proactive refresh failed:',
      err?.response?.status,
      err?.response?.data || err?.message
    );
    return false;
  } finally {
    inFlight = false;
  }
};

/**
 * One-shot check used by tests or manual triggers: if within skew, refresh now.
 */
export const checkAndRefreshNow = async (
  backendUrl,
  { skewSeconds = 90, retry = true, force = false } = {}
) => {
  const token = authService.getToken();
  const refreshToken = authService.getRefreshToken();
  if (!token || !refreshToken) return false;
  const msLeft = authService.getTimeUntilExpiry();
  if (!force) {
    if (msLeft == null) return false;
    if (msLeft > skewSeconds * 1000) return false;
  }
  const ok = await refreshOnce(backendUrl);
  if (!ok && retry) {
    await new Promise((r) => setTimeout(r, jitter(100, 200)));
    const ok2 = await refreshOnce(backendUrl);
    if (!ok2 && onExpiredCallback) {
      authService.clearToken();
      authService.setLogoutFlag();
      try {
        onExpiredCallback();
      } catch (_) {}
    }
    return ok2;
  }
  return ok;
};

/**
 * Start the proactive refresher loop
 * @param {string} backendUrl
 * @param {{ skewSeconds?: number, intervalMs?: number, retry?: boolean, onExpired?: Function }} options
 */
export const startTokenRefresher = (backendUrl, options = {}) => {
  const {
    skewSeconds = 90,
    intervalMs = 15000,
    retry = true,
    onExpired = null,
  } = options;

  if (intervalId) return; // already running
  onExpiredCallback = onExpired;

  intervalId = setInterval(async () => {
    try {
      const token = authService.getToken();
      const refreshToken = authService.getRefreshToken();
      if (!token || !refreshToken) return;

      const msLeft = authService.getTimeUntilExpiry();
      if (msLeft == null) return; // unknown expiry

      if (msLeft <= skewSeconds * 1000) {
        // Optional: skip refresh when document is hidden to reduce churn
        // if (document?.hidden) return;

        const ok = await refreshOnce(backendUrl);
        if (!ok && retry) {
          // Small jittered retry once
          await new Promise((r) => setTimeout(r, jitter(400, 1200)));
          const ok2 = await refreshOnce(backendUrl);
          if (!ok2 && onExpiredCallback) {
            // Treat as hard failure
            authService.clearToken();
            authService.setLogoutFlag();
            try {
              onExpiredCallback();
            } catch (_) {}
          }
        }
      }
    } catch (e) {
      // Never let the loop crash
      console.warn('[tokenRefresher] loop error:', e?.message);
    }
  }, intervalMs);
};

export const stopTokenRefresher = () => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  inFlight = false;
};

export default startTokenRefresher;
